import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [adminInfo, setAdminInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      checkAdminAndFetchData();
    }
  }, [user]);

  const checkAdminAndFetchData = async () => {
    try {
      const adminCheck = await axios.get(`${API}/admin/check`, { withCredentials: true });
      
      if (!adminCheck.data.is_admin) {
        navigate('/dashboard');
        return;
      }

      setAdminInfo(adminCheck.data);
      
      const statsRes = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mentality-300"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mentality-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-mentality-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/mentality-logo.jpg" 
                alt="Mentality Logo" 
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-mentality-300 to-mentality-600 bg-clip-text text-transparent">
                  Admin Panel
                </h1>
                <p className="text-xs text-gray-500">Platform Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-1 md:space-x-2">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-xs md:text-sm">
                  <span className="hidden md:inline">Student View</span>
                  <span className="md:hidden">👤</span>
                </Button>
              </Link>
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="bg-mentality-100 text-mentality-700 text-xs md:text-sm">
                  <span className="hidden md:inline">Dashboard</span>
                  <span className="md:hidden">📊</span>
                </Button>
              </Link>
              <Link to="/admin/sessions">
                <Button variant="ghost" size="sm" className="text-xs md:text-sm">
                  <span className="hidden md:inline">Sessions</span>
                  <span className="md:hidden">📝</span>
                </Button>
              </Link>
              {adminInfo?.role === 'super_admin' && (
                <Link to="/admin/manage-admins">
                  <Button variant="ghost" size="sm" className="text-xs md:text-sm">
                    <span className="hidden md:inline">Admins</span>
                    <span className="md:hidden">👥</span>
                  </Button>
                </Link>
              )}
              <div className="flex items-center space-x-2 pl-2 border-l">
                <Avatar className="border-2 border-mentality-200 w-8 h-8 md:w-10 md:h-10">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-mentality-100 text-mentality-700 text-xs">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => logout().then(() => navigate('/'))}
                  className="border-mentality-200 hover:bg-mentality-50 hidden md:inline-flex"
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Admin Info */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome, {adminInfo?.role === 'super_admin' ? 'Super Admin' : 'Admin'}! 👑
          </h2>
          <p className="text-gray-600">
            Manage the Mentality wellness platform for your students
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-mentality-300 to-mentality-500 text-white border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardDescription className="text-mentality-50">Total Users</CardDescription>
              <CardTitle className="text-4xl font-bold">{stats?.total_users || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-mentality-50">Registered students</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardDescription className="text-teal-100">Total Sessions</CardDescription>
              <CardTitle className="text-4xl font-bold">{stats?.total_sessions || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-teal-100">Available sessions</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardDescription className="text-cyan-100">Completions</CardDescription>
              <CardTitle className="text-4xl font-bold">{stats?.total_completions || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-cyan-100">Sessions completed</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-mentality-400 to-mentality-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardDescription className="text-mentality-100">Mood Entries</CardDescription>
              <CardTitle className="text-4xl font-bold">{stats?.total_moods || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-mentality-100">Total logged</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white border-0 shadow-lg">
            <CardHeader className="pb-3">
              <CardDescription className="text-teal-100">New Users</CardDescription>
              <CardTitle className="text-4xl font-bold">{stats?.recent_users || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-teal-100">Last 7 days</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="border-2 border-mentality-200 shadow-lg hover:shadow-xl transition-all hover:border-mentality-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">📝</span>
                Manage Sessions
              </CardTitle>
              <CardDescription>
                Add, edit, or remove wellness sessions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/admin/sessions">
                <Button className="w-full bg-gradient-to-r from-mentality-300 to-mentality-500 hover:from-mentality-400 hover:to-mentality-600 text-white" size="lg">
                  Go to Sessions
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-200 shadow-lg hover:shadow-xl transition-all hover:border-purple-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">🌐</span>
                Manage Domains
              </CardTitle>
              <CardDescription>
                Configure allowed email domains for registration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/admin/domains">
                <Button className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white" size="lg">
                  Go to Domains
                </Button>
              </Link>
            </CardContent>
          </Card>

          {adminInfo?.role === 'super_admin' && (
            <Card className="border-2 border-teal-200 shadow-lg hover:shadow-xl transition-all hover:border-teal-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">👥</span>
                  Manage Admins
                </CardTitle>
                <CardDescription>
                  Add or remove admin access
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/admin/manage-admins">
                  <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white" size="lg">
                    Manage Admins
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          <Card className="border-2 border-cyan-200 shadow-lg hover:shadow-xl transition-all hover:border-cyan-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">👤</span>
                Student View
              </CardTitle>
              <CardDescription>
                See the platform as students see it
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/dashboard">
                <Button className="w-full bg-gradient-to-r from-cyan-500 to-mentality-400 hover:from-cyan-600 hover:to-mentality-500 text-white" size="lg">
                  Switch to Student
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
